import { Box, Text, Code } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { BaseBlockProps } from "./types";

// Extract text from content (handles nested structure)
const getTextContent = (content: unknown): string => {
  if (typeof content === "string") return content;
  if (typeof content === "object" && content !== null) {
    const c = content as Record<string, unknown>;
    if (typeof c.text === "string") return c.text;
  }
  return "";
};

export const TextBlock = ({ block }: BaseBlockProps) => {
  const content = getTextContent(block.content);
  const style = (block.metadata as { style?: string })?.style;

  // Handle special styles
  if (style === "bold") {
    return (
      <Text fontWeight="semibold" color="text.body.primary" fontSize="md">
        {content}
      </Text>
    );
  }

  if (style === "code") {
    return (
      <Code
        display="block"
        p="sm"
        bg="bg.section"
        borderRadius="component.md"
        fontSize="sm"
        whiteSpace="pre-wrap"
        color="text.body.primary"
      >
        {content}
      </Code>
    );
  }

  // Default: render as markdown
  return (
    <Box
      className="luna-text-block"
      sx={{
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: "body",
          fontWeight: "semibold",
          color: "text.body.primary",
          lineHeight: 1.3,
          mb: "sm",
        },
        "& h1": {
          fontSize: "xl",
        },
        "& h2": {
          fontSize: "lg",
        },
        "& h3": {
          fontSize: "md",
        },
        "& h4, & h5, & h6": {
          fontSize: "md",
        },
        "& p": {
          mb: "sm",
          lineHeight: 1.5,
          color: "text.body.primary",
          fontSize: "md",
        },
        "& p:last-child": {
          mb: 0,
        },
        "& strong": {
          fontWeight: "semibold",
        },
        "& ul, & ol": {
          pl: "lg",
          mb: "sm",
        },
        "& li": {
          mb: "xs",
          color: "text.body.primary",
          fontSize: "md",
        },
        "& a": {
          color: "p.600",
          textDecoration: "underline",
        },
        "& hr": {
          my: "md",
          borderColor: "border.default",
        },
        "& em": {
          fontStyle: "italic",
        },
        "& del": {
          textDecoration: "line-through",
          color: "text.body.tertiary",
        },
        "& blockquote": {
          borderLeftWidth: "3px",
          borderLeftColor: "border.default",
          pl: "md",
          ml: 0,
          my: "sm",
          color: "text.body.secondary",
          fontStyle: "italic",
        },
        "& code": {
          fontFamily: "mono",
          fontSize: "sm",
          bg: "bg.section",
          px: "xs",
          py: "2xs",
          borderRadius: "component.sm",
          color: "text.body.primary",
        },
        "& pre": {
          bg: "bg.section",
          p: "md",
          borderRadius: "component.md",
          overflowX: "auto",
          my: "sm",
        },
        "& pre code": {
          bg: "transparent",
          p: 0,
          borderRadius: 0,
        },
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          my: "sm",
          fontSize: "sm",
        },
        "& th, & td": {
          border: "1px solid",
          borderColor: "border.default",
          px: "sm",
          py: "xs",
          textAlign: "left",
        },
        "& th": {
          bg: "bg.section",
          fontWeight: "semibold",
        },
        "& img": {
          maxWidth: "100%",
          borderRadius: "component.md",
          my: "sm",
        },
        "& input[type='checkbox']": {
          mr: "xs",
        },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{content}</ReactMarkdown>
    </Box>
  );
};
